import dayjs from 'dayjs';
import { AgreementStatus } from './useAgreementGate';

export const formatAgreementDate = (value?: string) => (value ? dayjs(value).format('D MMM YYYY') : '');

/** Calendar days from today until the date: 0 on the day itself, 1 the day before. */
export const daysLeft = (value?: string) =>
  value ? Math.max(0, dayjs(value).startOf('day').diff(dayjs().startOf('day'), 'day')) : 0;

const plural = (days: number) => (days === 1 ? '1 day' : `${days} days`);

export interface AgreementCopy {
  /** Top-of-page banner; null when nothing is shown. */
  banner: string | null;
  /** Short status on the profile page card. */
  card: string;
  action: string;
  /** Amber for anything the partner has to act on now, grey for reminders. */
  urgent: boolean;
}

/** Banner and profile card wording for each agreement state; null when nothing is shown at all. */
export const agreementCopy = (
  status: AgreementStatus | undefined,
  validUntil?: string,
  renewBy?: string,
): AgreementCopy | null => {
  switch (status) {
    case 'PENDING':
      return {
        banner: 'Please sign the partner agreement to access all features.',
        card: 'Pending',
        action: 'Sign Agreement',
        urgent: true,
      };
    case 'ACTIVE':
      return {
        banner: null,
        card: `Valid till ${formatAgreementDate(validUntil)}`,
        action: 'View Agreement',
        urgent: false,
      };
    case 'EXPIRING_SOON': {
      const days = daysLeft(validUntil);
      const when = days === 0 ? 'today' : `in ${plural(days)}`;
      return {
        banner: `Your partner agreement expires ${when}. Please renew it.`,
        card: `Expires ${when}`,
        action: 'Renew Agreement',
        urgent: false,
      };
    }
    case 'RENEWAL_DUE': {
      const days = daysLeft(renewBy);
      const within = days === 0 ? 'today' : `within ${plural(days)}`;
      return {
        banner: `Your partner agreement has expired. Please renew it ${within}.`,
        card: `Expired. Renew ${within}`,
        action: 'Renew Agreement',
        urgent: true,
      };
    }
    case 'EXPIRED':
      return {
        banner: 'The renewal date for your partner agreement has passed.',
        card: 'Renewal date passed',
        action: 'Renew Agreement',
        urgent: true,
      };
    default:
      // NOT_REQUIRED, and PREVIOUSLY_ACCEPTED (existing partners on the old clickwrap): nothing shown.
      return null;
  }
};
